
import { useState, useEffect } from "react";
import { FORM_MESSAGES, LABELS, UTILITY_CSS_CLASSES, DELETE_SUCCESS_FADEOUT_DURATION } from '../global/constants';
import { makeBEM, 
        fadeOutThenExecute, 
        getDefaultFormMsg, 
        fadeOutFormMsg,  
        gracefulFinally
} from '../global/utils';
import { createToDo, apiResultSuccess, editTodo, deleteToDo } from '../apihelper';
import FormStatus from "../atoms/FormStatus";
import FormButton from "../atoms/FormButton";
import { ConfirmDialog } from '../components/Dialogs';

function ToDoForm({ moduleName='todo-form', 
                    boardId=-1,
                    toDo,
                    discardForm,
                    deleteSuccess,
                    controller,
                 }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [formData, setFormData] = useState({ title: '' });
	const [errors, setErrors] = useState({});
	const [formStatus, setFormStatus] = useState( FORM_MESSAGES.toDoFormInstructions );
    const [editMode, setEditMode] = useState(false);
    const [fadeOutTimer, setFadeOutTimer] = useState(false);
    const [ctrl, setCtrl] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const formWrapperClass = `form ${moduleName}`;
    const inputsWrapperClass = `${makeBEM('form', 'inputs-wrapper')} ${makeBEM(moduleName, 'inputs-wrapper')}`;    
    const titleErrorClass = errors.title ? UTILITY_CSS_CLASSES.hasErrors : '';
	const titleInputClass = `input ${makeBEM(moduleName, 'title-input')} ${titleErrorClass}`;
    const showDeleteBtn = editMode;
    const btnsDisabled = isSubmitting || isDeleting;

    useEffect(() => {
        if ( toDo ) {
            setFormData(toDo);
            setEditMode(true);
        }
    }, [toDo])

    useEffect(() => {
        if ( controller ) {
            setCtrl(controller);
        }
    }, [controller])

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedFormData = { ...formData, [name]: value };
        setFormData(updatedFormData);
    }

    const validateForm = () => {
		const newErrors = {};

		if ( !formData.title ) {
			newErrors.title = FORM_MESSAGES.titleRequired;
		}

		return newErrors;
	}

    // Submits form, calls API to create a new Todo
    // New Todo result gets appended to controller
    // Then fades out form
    const submitNew = (postData) => {
        setIsSubmitting(true);
        setFormStatus( FORM_MESSAGES.newToDoItemSubmit );

        try {
            createToDo(
                (result) => {
                    if ( apiResultSuccess(result) ) {
                        setFormStatus( FORM_MESSAGES.createdNewToDo );

                        if ( result.todo ) {
                            if ( ctrl ) {
                                ctrl.addNew(result.todo);
                            }
                        }

                        setFadeOutTimer(
                            fadeOutThenExecute(discardForm)
                        );
                    }

                },
                postData,
            );
        } catch (error) {
            setFormStatus( FORM_MESSAGES.failedSubmit );
        }   finally {
            gracefulFinally(setIsSubmitting);
        }
    }

    const submitEdit = (postData) => {
        setIsSubmitting(true);
        setFormStatus( FORM_MESSAGES.editTodoSubmit );

        postData = { ...postData, todo_id: postData.id };

        try {
            editTodo(
                (result) => {
                    if ( apiResultSuccess(result) ) {
                        setFormStatus( FORM_MESSAGES.savedToDo );

                        if ( result.todo ) {
                            if ( ctrl ) {
                                ctrl.editTodo(result.todo);
                            }
                            fadeOutFormMsg(setFormStatus, '');
                        }
                    }
                },
                postData
            );

        } catch (error) {
            setFormStatus( FORM_MESSAGES.failedSubmit );
        } finally {
            gracefulFinally(setIsSubmitting);
        }
    }


    const handleSubmit = (e) => {
        e.preventDefault();

        const validationErrors = validateForm();

        if ( Object.keys(validationErrors).length > 0 ) {
			setErrors(validationErrors);
        } else {
            setErrors({});

            const postData = { ...formData, board_id: boardId} 

            if ( !editMode ) {
                submitNew(postData);
            } else {
                submitEdit(postData);
            }
        }
        
    }

    const getFormStatus = () => {
		if ( errors.title ) {
			return errors.title
		}

		if ( formStatus ) {
			return formStatus;
		}

        return getDefaultFormMsg(editMode, FORM_MESSAGES.toDoEdit, FORM_MESSAGES.toDoFormInstructions);
	}

    const resetForm = () => {
        setFormData({ title: ''});
        setErrors({});
    }

    const handleDiscard = () => {
        resetForm();
        discardForm();

        if ( fadeOutTimer ) {
            clearTimeout(fadeOutTimer);
        }
    }

    const handleDelete = () => {
        discardConfirmDialog();
        const postData = { ...formData, todo_id: formData.id };
        setIsDeleting(true);

        try {
            deleteToDo(
                (result) => {
                    if ( apiResultSuccess(result) ) {
                        setFormStatus( FORM_MESSAGES.deletedToDo );
                        if ( deleteSuccess ) {
                            setFadeOutTimer(
                                fadeOutThenExecute(() => {
                                    deleteSuccess(postData.todo_id);
                                })
                            );
                        }
                    } else {
                        setFormStatus( result.message );
                    }
                },
                postData
            );
        } catch {
            setFormStatus( FORM_MESSAGES.failedSubmit );
        } finally {
            gracefulFinally(setIsDeleting);
        }
    }

    const promptDeleteConfirm = () => {
        setShowConfirmDialog(true);
    }

    const discardConfirmDialog = () => {
        setShowConfirmDialog(false);
    }

    return (
        <div className={ formWrapperClass }>
            <form onSubmit={ handleSubmit }>
                <div className={ inputsWrapperClass  }>
                    <FormStatus
                        errors={errors}
                        formStatus= { getFormStatus() }
                        editMode={editMode}
                        customClass={ makeBEM(moduleName, 'form-status') }
                    />
                
                    <input
                        id="title"
                        name="title"
                        type="text"
                        className={ titleInputClass }
                        onChange={ handleChange }
                        autoComplete="off"
                        required
                        placeholder={ LABELS.titlePlaceholder }
                        value={ formData.title }
                        autoFocus
                    />

                    <div className={ `${makeBEM('form', 'btns-wrapper')} ${makeBEM(moduleName, 'btns-wrapper')}` }>
                        <FormButton
                            customClass={ makeBEM(moduleName, 'submit-btn') }
                            type="submit"
                            onClick={handleSubmit}
                            disabled={btnsDisabled}
                            isSubmitting={isSubmitting}
                        />
                        <FormButton
                            customClass={ makeBEM(moduleName, 'discard-btn') }
                            onClick={handleDiscard}
                            disabled={btnsDisabled}
                            type="button"
                            icon="close"
                        />
                        {
                            showDeleteBtn &&
                            <FormButton
                                customClass={ makeBEM(moduleName, 'delete-btn') }
                                onClick={promptDeleteConfirm}
                                disabled={btnsDisabled}
                                isSubmitting={isDeleting}
                                type="button"
                                icon="delete"
                            />
                        }
                    </div>
                </div>
            </form>

            {
                showConfirmDialog &&
                <ConfirmDialog
                    show={showConfirmDialog}
                    message={LABELS.confirmDelete}
                    confirmAction={handleDelete}
                    discardAction={discardConfirmDialog}
                />
            }
        </div>
    )
}

export default ToDoForm;